import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { Tooltip, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  PiMapTrifoldBold,
  PiTrashBold,
  PiFolderBold,
  PiCalendarBlankBold,
  PiNotePencilBold,
} from 'react-icons/pi';
import type { CollectForm_Base } from '~/common/typescript/models/CollectForm';
import type { Project_Base } from '~/common/typescript/models/Project';
import { parseFormContent } from '~/components/Map/panels/GeocollectDatatablePanel/utils/CollectFormParser';
import { formatDate } from '~/utils/dateUtils';
import Routes from '~/routes/routes';
import defaultFormBg from '~/assets/images/collection_image.png';

export interface CollectFormItemProps {
  form: CollectForm_Base;
  project?: Project_Base;
  onViewDetail: (form: CollectForm_Base) => void;
  onDelete: (uuid: string) => void;
}

const CollectFormItem = ({
  form,
  project,
  onViewDetail,
  onDelete,
}: CollectFormItemProps) => {
  const navigate = useNavigate();

  const parsed = useMemo(() => parseFormContent(form), [form]);
  const fieldCount = parsed?.fields?.length || 0;
  const isV2 = form.form_version === 2 || parsed?.version === 'v2';

  const projectName = project ? project.name : `Dự án #${form.project_id}`;
  const thumbnailSrc = project?.avatar || project?.image || defaultFormBg;

  const handleCardClick = () => {
    onViewDetail(form);
  };

  const handleOpenMap = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`${Routes.PROJECT}/${form.project_id}`);
  };

  const handleOpenDesign = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open in form builder/design screen
    navigate(`${Routes.COLLECT_FORM_DESIGN}?formUuid=${form.uuid}&projectId=${form.project_id}`);
  };

  return (
    <UI_Wrapper key={form.uuid} onClick={handleCardClick}>
      <UI_BGImage
        src={thumbnailSrc}
        alt={form.name}
        onError={(e) => {
          (e.target as HTMLImageElement).src = defaultFormBg;
        }}
      />

      {/* Top badges */}
      <UI_TopRightBadges>
        <UI_VersionBadge $isV2={isV2}>
          {isV2 ? 'V2' : 'V1'}
        </UI_VersionBadge>
        <UI_FieldsBadge>
          {fieldCount} trường
        </UI_FieldsBadge>
      </UI_TopRightBadges>

      {/* Top-left Quick Action Icons */}
      <UI_TopLeftActions onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Mở trong bản đồ dự án">
          <UI_ActionBtn onClick={handleOpenMap}>
            <PiMapTrifoldBold size={13} />
          </UI_ActionBtn>
        </Tooltip>

        <Tooltip title="Thiết kế biểu mẫu">
          <UI_ActionBtn onClick={handleOpenDesign}>
            <PiNotePencilBold size={13} />
          </UI_ActionBtn>
        </Tooltip>

        <Popconfirm
          title="Xoá biểu mẫu"
          description="Bạn có chắc chắn muốn xoá biểu mẫu này không?"
          onConfirm={() => onDelete(form.uuid)}
          okText="Xoá"
          cancelText="Huỷ"
          okButtonProps={{ danger: true }}
        >
          <UI_ActionBtn $danger title="Xoá biểu mẫu">
            <PiTrashBold size={13} />
          </UI_ActionBtn>
        </Popconfirm>
      </UI_TopLeftActions>

      {/* Bottom overlay with title, description, and metadata */}
      <UI_Overlay>
        <UI_Name title={form.name}>{form.name}</UI_Name>
        <UI_Description title={form.description || ''}>
          {form.description || 'Không có mô tả'}
        </UI_Description>

        <UI_MetaRow>
          <UI_ProjectTag title={`Dự án: ${projectName}`}>
            <PiFolderBold style={{ fontSize: 13, flexShrink: 0 }} />
            <span>{projectName}</span>
          </UI_ProjectTag>

          <UI_DateTag title={`Ngày tạo: ${formatDate(form.created_at)}`}>
            <PiCalendarBlankBold style={{ fontSize: 12, flexShrink: 0 }} />
            <span>{formatDate(form.created_at)}</span>
          </UI_DateTag>
        </UI_MetaRow>
      </UI_Overlay>
    </UI_Wrapper>
  );
};

export default memo(CollectFormItem);

const UI_Wrapper = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);

    img {
      transform: scale(1.04);
    }
  }
`;

const UI_BGImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const UI_TopRightBadges = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UI_VersionBadge = styled.div<{ $isV2?: boolean }>`
  background: ${(props) =>
    props.$isV2
      ? 'linear-gradient(135deg, rgba(8, 145, 178, 0.92) 0%, rgba(2, 132, 199, 0.92) 100%)'
      : 'rgba(51, 65, 85, 0.88)'};
  backdrop-filter: blur(4px);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
`;

const UI_FieldsBadge = styled.div`
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(4px);
  color: #f1f5f9;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.12);
  white-space: nowrap;
`;

const UI_TopLeftActions = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.9;
  transition: opacity 0.2s ease;

  ${UI_Wrapper}:hover & {
    opacity: 1;
  }
`;

const UI_ActionBtn = styled.button<{ $danger?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  color: ${(props) => (props.$danger ? '#f87171' : '#f8fafc')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);

  &:hover {
    background: ${(props) =>
      props.$danger ? 'rgba(239, 68, 68, 0.9)' : 'rgba(37, 99, 235, 0.9)'};
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const UI_Overlay = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 10px 12px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0) 0%,
    rgba(15, 23, 42, 0.65) 25%,
    rgba(15, 23, 42, 0.94) 100%
  );
  display: flex;
  flex-direction: column;
  gap: 5px;
  box-sizing: border-box;
  z-index: 1;
`;

const UI_Name = styled.h3`
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

const UI_Description = styled.p`
  color: #cbd5e1;
  font-size: 12px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  line-height: 1.3;
`;

const UI_MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
  min-width: 0;
`;

const UI_ProjectTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: #e2e8f0;
  font-size: 11px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const UI_DateTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  color: #e2e8f0;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 4px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
  white-space: nowrap;
`;
